import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, GoogleMap } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "300px",
};

const mapId = process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID";

const LocationPicker = ({ onLocationSelect, hideButtons = false, externalPosition = null }) => {
  const [position, setPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [currentAddress, setCurrentAddress] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [tooltipPixelPosition, setTooltipPixelPosition] = useState({ x: 0, y: 0 });
  const [mapZoom, setMapZoom] = useState(15);
  const userHasZoomedRef = useRef(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapContainerRef = useRef(null);
  const positionUpdateIntervalRef = useRef(null);
  const tooltipOverlayRef = useRef(null);
  const addressTimeoutRef = useRef(null);
  const currentGeocodeRequestRef = useRef(null);
  const isSearchingAddressRef = useRef(false);
  const tooltipTextRef = useRef("");
  const positionRef = useRef(null);
  const fullscreenTimeoutsRef = useRef([]);
  const isDraggingRef = useRef(false);
  const lastDragPositionRef = useRef(null);
  const hasSetInitialCenterRef = useRef(false);
  const lastExternalPositionRef = useRef(null);
  const mapCenterKeyRef = useRef(0);

  useEffect(() => {
    // Check if Google Maps is fully loaded by App.js (including Map constructor and marker library)
    const checkGoogleMapsLoaded = async () => {
      if (!window.google?.maps?.Map || !window.google?.maps?.places) {
        return false;
      }
      
      // If mapId is set, ensure marker library is loaded for AdvancedMarkerElement
      if (mapId && !window.google?.maps?.marker?.AdvancedMarkerElement) {
        try {
          await window.google.maps.importLibrary("marker");
        } catch (err) {
          console.error("Failed to load marker library", err);
        }
      }
      
      // Ensure Geocoder library is loaded
      if (!window.google?.maps?.Geocoder) {
        try {
          await window.google.maps.importLibrary("geocoding");
        } catch (err) {
          console.error("Failed to load geocoding library", err);
        }
      }
      
      return true;
    };

    const initializeLoader = async () => {
      if (await checkGoogleMapsLoaded()) {
        setIsLoaded(true);
      } else {
        const checkInterval = setInterval(async () => {
          if (await checkGoogleMapsLoaded()) {
            setIsLoaded(true);
            clearInterval(checkInterval);
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }
    };

    initializeLoader();
  }, []);

  // Create custom OverlayView for tooltip that works in fullscreen
  const createTooltipOverlay = (coords, text) => {
    if (!mapRef.current || !window.google?.maps?.OverlayView) return null;

    class TooltipOverlay extends window.google.maps.OverlayView {
      constructor(position, content) {
        super();
        this.position = position;
        this.content = content;
        this.div = null;
      }

      onAdd() {
        // Create wrapper div to allow arrow to overflow while text stays contained
        this.div = document.createElement('div');
        this.div.style.cssText = `
          position: absolute;
          pointer-events: none;
          z-index: 2147483647;
          transform: translateX(-50%);
        `;
        
        // Create inner div for the tooltip bubble (overflow visible to show arrow)
        const tooltipBubble = document.createElement('div');
        tooltipBubble.style.cssText = `
          position: relative;
          background-color: #333333;
          color: white;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
          text-align: center;
          line-height: 1.4;
          display: inline-block;
          overflow: visible;
        `;
        
        // Create text container with overflow control
        const textContainer = document.createElement('div');
        textContainer.innerHTML = this.content;
        textContainer.style.cssText = `
          display: block;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `;
        tooltipBubble.appendChild(textContainer);
        
        // Add arrow pointing down (positioned relative to bubble, can overflow)
        const arrow = document.createElement('div');
        arrow.style.cssText = `
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid #333333;
          pointer-events: none;
        `;
        tooltipBubble.appendChild(arrow);
        
        this.div.appendChild(tooltipBubble);

        const panes = this.getPanes();
        if (panes) {
          panes.overlayMouseTarget.appendChild(this.div);
        }
      }

      draw() {
        const overlayProjection = this.getProjection();
        if (!overlayProjection || !this.position) return;

        const point = overlayProjection.fromLatLngToDivPixel(
          new window.google.maps.LatLng(this.position.lat, this.position.lng)
        );

        if (this.div && point) {
          this.div.style.left = point.x + 'px';
          this.div.style.top = (point.y - 50) + 'px';
        }
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }

      updatePosition(newPosition) {
        if (!newPosition) return;
        // CRITICAL: Create a new object to ensure position change is detected
        this.position = {
          lat: newPosition.lat,
          lng: newPosition.lng
        };
        // Force redraw - use requestAnimationFrame to ensure map projection is ready
        if (this.getProjection()) {
          this.draw();
        } else {
          // If projection not ready, retry after a short delay
          setTimeout(() => {
            if (this.getProjection() && this.position) {
              this.draw();
            }
          }, 100);
        }
      }

      updateContent(newContent) {
        this.content = newContent;
        if (this.div) {
          // Find the tooltip bubble element (first child)
          const tooltipBubble = this.div.firstElementChild;
          if (tooltipBubble) {
            // Find the text container (first child of bubble, before arrow)
            const textContainer = tooltipBubble.firstElementChild;
            if (textContainer && textContainer.tagName === 'DIV') {
              textContainer.innerHTML = newContent;
            } else {
              // Fallback: update bubble directly
              tooltipBubble.innerHTML = newContent;
            }
          } else {
            // Fallback: if structure changed, update the div directly
            this.div.innerHTML = newContent;
          }
        }
      }
    }

    const overlay = new TooltipOverlay(coords, text);
    overlay.setMap(mapRef.current);
    return overlay;
  };

  const updateTooltipPosition = (coords) => {
    if (!mapRef.current || !coords) return;
    setTooltipPosition(coords);
    
    // Update OverlayView if it exists, or create it if it doesn't
    if (tooltipOverlayRef.current) {
      tooltipOverlayRef.current.updatePosition(coords);
      // Force redraw to ensure it's visible, especially after fullscreen changes
      if (tooltipOverlayRef.current.draw) {
        tooltipOverlayRef.current.draw();
      }
    } else {
      // Create new overlay if it doesn't exist
      const currentText = tooltipText || "Searching address…";
      tooltipOverlayRef.current = createTooltipOverlay(coords, currentText);
    }
    
    // Also update pixel position for fallback
    updateTooltipPixelPosition(coords);
  };

  const updateTooltipPixelPosition = (coords) => {
    if (!mapRef.current || !coords || !mapContainerRef.current) return;
    
    // Use requestAnimationFrame to ensure map is fully rendered
    requestAnimationFrame(() => {
      if (!mapRef.current || !mapContainerRef.current) return;
      
      // Trigger resize to ensure map projection is updated (critical for fullscreen)
      if (window.google?.maps?.event) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
      
      // Wait a bit for the map to update after resize
      setTimeout(() => {
        if (!mapRef.current || !mapContainerRef.current) return;
        
        const projection = mapRef.current.getProjection();
        if (!projection) {
          // Retry if projection not ready (common in fullscreen transitions)
          setTimeout(() => {
            if (mapRef.current && mapRef.current.getProjection() && mapContainerRef.current) {
              updateTooltipPixelPosition(coords);
            }
          }, 100);
          return;
        }
        
        try {
          const latLng = new window.google.maps.LatLng(coords.lat, coords.lng);
          
          // Get the map's actual div element
          const mapDiv = mapRef.current.getDiv();
          if (!mapDiv) return;
          
          // Get container dimensions - use getBoundingClientRect for accurate fullscreen dimensions
          const containerRect = mapContainerRef.current.getBoundingClientRect();
          const mapDivRect = mapDiv.getBoundingClientRect();
          const mapWidth = containerRect.width || mapDiv.offsetWidth || mapDiv.clientWidth;
          const mapHeight = containerRect.height || mapDiv.offsetHeight || mapDiv.clientHeight;
          
          if (mapWidth === 0 || mapHeight === 0) {
            // Container not ready, retry
            setTimeout(() => {
              if (mapRef.current && mapContainerRef.current) {
                updateTooltipPixelPosition(coords);
              }
            }, 100);
            return;
          }
          
          // Try to use fromLatLngToDivPixel if available (works better in fullscreen)
          let pixelPoint = null;
          if (projection.fromLatLngToDivPixel) {
            try {
              const divPixel = projection.fromLatLngToDivPixel(latLng);
              if (divPixel) {
                // fromLatLngToDivPixel returns coordinates relative to map div
                // We need to adjust for the map div's position within the container
                const mapDivOffsetX = mapDivRect.left - containerRect.left;
                const mapDivOffsetY = mapDivRect.top - containerRect.top;
                pixelPoint = {
                  x: divPixel.x + mapDivOffsetX,
                  y: divPixel.y + mapDivOffsetY
                };
              }
            } catch (e) {
              // Fallback to manual calculation
            }
          }
          
          // If fromLatLngToDivPixel didn't work, use manual calculation
          if (!pixelPoint) {
            // Convert lat/lng to world coordinates (0-1 range)
            const worldCoordinate = projection.fromLatLngToPoint(latLng);
            
            if (!worldCoordinate) return;
            
            // Get current map bounds
            const bounds = mapRef.current.getBounds();
            if (!bounds) {
              // Retry if bounds not ready
              setTimeout(() => {
                if (mapRef.current && mapRef.current.getBounds() && mapContainerRef.current) {
                  updateTooltipPixelPosition(coords);
                }
              }, 100);
              return;
            }
            
            // Get the top-left and bottom-right corners in world coordinates
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const topRight = projection.fromLatLngToPoint(ne);
            const bottomLeft = projection.fromLatLngToPoint(sw);
            
            // Calculate scale factors
            const scaleX = mapWidth / (topRight.x - bottomLeft.x);
            const scaleY = mapHeight / (bottomLeft.y - topRight.y);
            
            // Convert world coordinates to pixel coordinates relative to container
            // Note: Y is inverted in Google Maps (top has higher Y value)
            pixelPoint = {
              x: (worldCoordinate.x - bottomLeft.x) * scaleX,
              y: (worldCoordinate.y - topRight.y) * scaleY
            };
          }
          
          // Validate pixel coordinates
          if (!pixelPoint || isNaN(pixelPoint.x) || isNaN(pixelPoint.y) || !isFinite(pixelPoint.x) || !isFinite(pixelPoint.y)) {
            return;
          }
          
          setTooltipPixelPosition({ x: pixelPoint.x, y: pixelPoint.y });
        } catch (error) {
          console.error("Error calculating tooltip position:", error);
          // Retry on error
          setTimeout(() => {
            if (mapRef.current && mapContainerRef.current) {
              updateTooltipPixelPosition(coords);
            }
          }, 100);
        }
      }, 50);
    });
  };

  const attachMarker = (map, coords) => {
    if (!map || !coords) return;

    const { google } = window;
    
    if (!markerRef.current) {
      // Always try to use AdvancedMarkerElement with mapId
      if (google?.maps?.marker?.AdvancedMarkerElement) {
        try {
          markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: coords,
            draggable: true,
            gmpDraggable: true,
          });

            // Add drag event listeners using google.maps.event for compatibility
            const dragStartListener = google.maps.event.addListener(markerRef.current, "dragstart", () => {
              console.log("🔴 [DRAG START] Drag start event fired");
              isDraggingRef.current = true;
              
              // Cancel any pending address display timeout
              if (addressTimeoutRef.current) {
                clearTimeout(addressTimeoutRef.current);
                addressTimeoutRef.current = null;
              }
              
              // Cancel any pending geocoding request
              if (currentGeocodeRequestRef.current) {
                // Mark the request as cancelled
                currentGeocodeRequestRef.current = null;
              }
              
              const searchingText = "Searching address…";
              setTooltipText(searchingText);
              setIsSearchingAddress(true);
              
              // Get current marker position
              let currentCoords = coords;
              if (markerRef.current && markerRef.current.position) {
                const pos = markerRef.current.position;
                currentCoords = {
                  lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
                  lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng,
                };
              }
              
              // Ensure overlay exists and shows "Searching address…"
              if (tooltipOverlayRef.current) {
                tooltipOverlayRef.current.updateContent(searchingText);
                tooltipOverlayRef.current.updatePosition(currentCoords);
              } else if (mapRef.current) {
                // Create overlay if it doesn't exist
                tooltipOverlayRef.current = createTooltipOverlay(currentCoords, searchingText);
              }
              updateTooltipPosition(currentCoords);
            });

          const dragListener = google.maps.event.addListener(markerRef.current, "drag", (e) => {
            console.log("🔵 [DRAG] Drag event fired");
            let newCoords;
            if (e && e.latLng) {
              newCoords = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
              };
            } else if (markerRef.current && markerRef.current.position) {
              const pos = markerRef.current.position;
              newCoords = {
                lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
                lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng,
              };
            }
            
            if (newCoords) {
              console.log("🔵 [DRAG] New coords:", newCoords);
              // Create a new position object to ensure state updates are detected
              const positionObj = { lat: newCoords.lat, lng: newCoords.lng };
              
              console.log("🔵 [DRAG] Current state position:", position);
              console.log("🔵 [DRAG] Setting position state to:", positionObj);
              // Update position state immediately to keep everything in sync
              setPosition(positionObj);
              setTooltipPosition(positionObj);
              
              // CRITICAL: Update overlay position directly to ensure it's always in sync
              if (tooltipOverlayRef.current) {
                // Set position directly AND call updatePosition to ensure both are updated
                // Create new object so overlay detects the change
                tooltipOverlayRef.current.position = { lat: positionObj.lat, lng: positionObj.lng };
                tooltipOverlayRef.current.updatePosition(positionObj);
              } else if (mapRef.current) {
                // Create overlay if it doesn't exist during drag
                const searchingText = "Searching address…";
                tooltipOverlayRef.current = createTooltipOverlay(positionObj, searchingText);
              }
              
              // Also update pixel position
              updateTooltipPixelPosition(positionObj);
            }
          });

          const dragEndListener = google.maps.event.addListener(markerRef.current, "dragend", (e) => {
            console.log("🟢 [DRAG END] Drag end event fired");
            
            // Preserve current zoom level - don't reset zoom when dragging
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== null && currentZoom !== undefined) {
                setMapZoom(currentZoom);
                userHasZoomedRef.current = true;
              }
            }
            
            let newCoords;
            if (e && e.latLng) {
              newCoords = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
              };
            } else if (markerRef.current && markerRef.current.position) {
              const pos = markerRef.current.position;
              newCoords = {
                lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
                lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng,
              };
            } else {
              newCoords = coords;
            }
            
            console.log("🟢 [DRAG END] New coords from event:", newCoords);
            console.log("🟢 [DRAG END] Current state position:", position);
            console.log("🟢 [DRAG END] Marker position before update:", markerRef.current?.position);
            
            // CRITICAL: Store the dragged position in ref to prevent it from being overwritten
            const positionObj = { lat: newCoords.lat, lng: newCoords.lng };
            lastDragPositionRef.current = { ...positionObj, _timestamp: Date.now() };
            isDraggingRef.current = false;
            
            // Cancel any pending requests and immediately show "Searching address…"
            if (addressTimeoutRef.current) {
              clearTimeout(addressTimeoutRef.current);
              addressTimeoutRef.current = null;
            }
            if (currentGeocodeRequestRef.current) {
              currentGeocodeRequestRef.current = null;
            }
            
            // Immediately show "Searching address…" - same as handleMapClick
            const searchingText = "Searching address…";
            setTooltipText(searchingText);
            setIsSearchingAddress(true);
            
            // Ensure overlay exists and shows "Searching address…"
            if (tooltipOverlayRef.current) {
              tooltipOverlayRef.current.updateContent(searchingText);
              tooltipOverlayRef.current.updatePosition(newCoords);
            } else if (mapRef.current) {
              // Create overlay if it doesn't exist
              tooltipOverlayRef.current = createTooltipOverlay(newCoords, searchingText);
            }
            
            // CRITICAL: Update position state FIRST to ensure marker position persists
            // Use setTimeout to ensure this happens after any other state updates
            setTimeout(() => {
              console.log("🟢 [DRAG END] Setting position state to (delayed):", positionObj);
              setPosition(positionObj);
              positionRef.current = positionObj;
              
              // CRITICAL: Also update marker position directly to ensure it persists
              // This is especially important when exiting fullscreen
              if (markerRef.current) {
                if (markerRef.current.position) {
                  // For AdvancedMarkerElement, create a new LatLng object
                  if (window.google?.maps?.LatLng) {
                    const newLatLng = new window.google.maps.LatLng(positionObj.lat, positionObj.lng);
                    console.log("🟢 [DRAG END] Setting marker position to LatLng (delayed):", newLatLng);
                    markerRef.current.position = newLatLng;
                  } else {
                    console.log("🟢 [DRAG END] Setting marker position to object (delayed):", positionObj);
                    markerRef.current.position = positionObj;
                  }
                } else if (markerRef.current.setPosition) {
                  console.log("🟢 [DRAG END] Using setPosition method (delayed):", positionObj);
                  markerRef.current.setPosition(positionObj);
                }
                console.log("🟢 [DRAG END] Marker position after update (delayed):", markerRef.current.position);
              }
            }, 0);
            
            // Update tooltip and trigger geocoding
            updateTooltipPosition(positionObj);
            reverseGeocodeForTooltip(positionObj);
            console.log("🟢 [DRAG END] Completed - position state should be:", positionObj);
          });

          // Store listeners for cleanup
          markerRef.current._listeners = [dragStartListener, dragListener, dragEndListener];
          
          // Initialize tooltip when marker is first created
          setTooltipText("Searching address…");
          setIsSearchingAddress(true);
          // Create tooltip overlay
          tooltipOverlayRef.current = createTooltipOverlay(coords, "Searching address…");
          updateTooltipPosition(coords);
          reverseGeocodeForTooltip(coords);
        } catch (err) {
          console.error("Failed to create AdvancedMarkerElement", err);
        }
      } else {
        console.warn("AdvancedMarkerElement not available. Please ensure marker library is loaded and REACT_APP_GOOGLE_MAP_ID is set.");
      }
    } else {
      // Update position for existing marker
      console.log("🟠 [ATTACH MARKER] Updating existing marker position");
      console.log("🟠 [ATTACH MARKER] New coords:", coords);
      console.log("🟠 [ATTACH MARKER] Current marker position:", markerRef.current.position);
      
      // CRITICAL: For AdvancedMarkerElement, we need to update position properly
      if (markerRef.current.position) {
        // Create a new LatLng object to ensure the position is properly updated
        const newLatLng = new window.google.maps.LatLng(coords.lat, coords.lng);
        console.log("🟠 [ATTACH MARKER] Setting marker position to LatLng:", newLatLng);
        markerRef.current.position = newLatLng;
      } else if (markerRef.current.setPosition) {
        console.log("🟠 [ATTACH MARKER] Using setPosition method:", coords);
        markerRef.current.setPosition(coords);
      }
      
      // Also ensure the marker's internal position is updated
      // This is critical for AdvancedMarkerElement to persist position changes
      if (markerRef.current.position && typeof markerRef.current.position.lat === 'function') {
        // Position is a LatLng object, ensure it's updated
        const currentLat = markerRef.current.position.lat();
        const currentLng = markerRef.current.position.lng();
        console.log("🟠 [ATTACH MARKER] Current marker position (from LatLng):", { lat: currentLat, lng: currentLng });
        if (Math.abs(currentLat - coords.lat) > 0.000001 || Math.abs(currentLng - coords.lng) > 0.000001) {
          // Position doesn't match, force update
          console.log("🟠 [ATTACH MARKER] Position mismatch, forcing update");
          markerRef.current.position = new window.google.maps.LatLng(coords.lat, coords.lng);
        }
      }
      
      console.log("🟠 [ATTACH MARKER] Marker position after update:", markerRef.current.position);
      updateTooltipPosition(coords);
    }
  };

  const onLoadAutocomplete = (instance) => setAutocomplete(instance);

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place?.geometry) return;

    const coords = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setPosition(coords);
    const address = place.formatted_address || `${coords.lat}, ${coords.lng}`;
    setCurrentAddress(address);
    const tooltipContent = `📍 ${address}`;
    setTooltipText(tooltipContent);
    setIsSearchingAddress(false);
    
    // When using autocomplete search, it's okay to set zoom since it's an intentional search action
    if (mapRef.current) {
      mapRef.current.panTo(coords);
      mapRef.current.setZoom(15);
      setMapZoom(15);
      // Reset the flag since autocomplete is an intentional action
      userHasZoomedRef.current = false;
    }
    
    // Update or create overlay
    if (tooltipOverlayRef.current) {
      tooltipOverlayRef.current.updateContent(tooltipContent);
      tooltipOverlayRef.current.updatePosition(coords);
    } else {
      tooltipOverlayRef.current = createTooltipOverlay(coords, tooltipContent);
    }
    updateTooltipPosition(coords);
    onLocationSelect?.(coords, address);
  };

  const handleMapClick = async (event) => {
    const coords = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    
    // Preserve current zoom level - don't reset zoom when clicking
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom !== null && currentZoom !== undefined) {
        setMapZoom(currentZoom);
        userHasZoomedRef.current = true;
      }
    }
    
    // Cancel any pending requests and immediately show "Searching address…"
    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
      addressTimeoutRef.current = null;
    }
    if (currentGeocodeRequestRef.current) {
      currentGeocodeRequestRef.current = null;
    }
    
    // Immediately show "Searching address…"
    const searchingText = "Searching address…";
    setTooltipText(searchingText);
    setIsSearchingAddress(true);
    if (tooltipOverlayRef.current) {
      tooltipOverlayRef.current.updateContent(searchingText);
    }
    
    setPosition(coords);
    reverseGeocodeForTooltip(coords);
    
    // Automatically update location in text box when user clicks on map
    if (window.google?.maps) {
      // Ensure Geocoder is available, load it if needed
      let Geocoder = window.google.maps.Geocoder;
      if (!Geocoder) {
        try {
          await window.google.maps.importLibrary("geocoding");
          Geocoder = window.google.maps.Geocoder;
        } catch (err) {
          console.error("Failed to load geocoding library", err);
          const address = `${coords.lat}, ${coords.lng}`;
          setCurrentAddress(address);
          onLocationSelect?.(coords, address);
          return;
        }
      }
      
      if (Geocoder) {
        const geocoder = new Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          const address = (status === "OK" && results[0]) 
            ? results[0].formatted_address 
            : `${coords.lat}, ${coords.lng}`;
          setCurrentAddress(address);
          onLocationSelect?.(coords, address);
        });
      } else {
        const address = `${coords.lat}, ${coords.lng}`;
        setCurrentAddress(address);
        onLocationSelect?.(coords, address);
      }
    } else {
      const address = `${coords.lat}, ${coords.lng}`;
      setCurrentAddress(address);
      onLocationSelect?.(coords, address);
    }
  };

  const reverseGeocodeForTooltip = async (coords) => {
    // Cancel any pending address display timeout
    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
      addressTimeoutRef.current = null;
    }
    
    // Cancel any pending geocoding request
    if (currentGeocodeRequestRef.current) {
      currentGeocodeRequestRef.current = null;
    }
    
    // Store current request ID to track if this request is still valid
    const requestId = Symbol();
    currentGeocodeRequestRef.current = requestId;
    
    const searchingText = "Searching address…";
    setTooltipText(searchingText);
    setIsSearchingAddress(true);
    // Update overlay content immediately
    if (tooltipOverlayRef.current) {
      tooltipOverlayRef.current.updateContent(searchingText);
    }
    updateTooltipPosition(coords);

    if (window.google?.maps) {
      // Ensure Geocoder is available, load it if needed
      let Geocoder = window.google.maps.Geocoder;
      if (!Geocoder) {
        try {
          await window.google.maps.importLibrary("geocoding");
          Geocoder = window.google.maps.Geocoder;
        } catch (err) {
          console.error("Failed to load geocoding library", err);
          // Check if this request is still valid
          if (currentGeocodeRequestRef.current !== requestId) {
            return; // Request was cancelled, don't update tooltip
          }
          setIsSearchingAddress(false);
          // Show address immediately without delay
          const address = `${coords.lat}, ${coords.lng}`;
          setTooltipText(address);
          // Update currentAddress and call onLocationSelect callback
          setCurrentAddress(address);
          onLocationSelect?.(coords, address);
          if (tooltipOverlayRef.current) {
            tooltipOverlayRef.current.updateContent(address);
          }
          return;
        }
      }
      
      if (Geocoder) {
        const geocoder = new Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          // Check if this request is still valid
          if (currentGeocodeRequestRef.current !== requestId) {
            return; // Request was cancelled, don't update tooltip
          }
          
          setIsSearchingAddress(false);
          
          // Show address immediately without delay
          const address = status === "OK" && results[0] 
            ? results[0].formatted_address
            : `${coords.lat}, ${coords.lng}`;
          setTooltipText(address);
          
          // Update currentAddress and call onLocationSelect callback
          setCurrentAddress(address);
          onLocationSelect?.(coords, address);
          
          // Update overlay content and position to ensure sync
          // CRITICAL: This must work even after exiting fullscreen
          if (tooltipOverlayRef.current) {
            tooltipOverlayRef.current.updateContent(address);
            tooltipOverlayRef.current.updatePosition(coords);
            // Force redraw to ensure visibility (especially important after fullscreen exit)
            if (tooltipOverlayRef.current.draw) {
              tooltipOverlayRef.current.draw();
            }
            // Trigger map resize to ensure overlay is properly positioned
            if (mapRef.current && window.google?.maps?.event) {
              window.google.maps.event.trigger(mapRef.current, 'resize');
              // Force another draw after resize
              setTimeout(() => {
                if (tooltipOverlayRef.current && tooltipOverlayRef.current.draw) {
                  tooltipOverlayRef.current.draw();
                }
              }, 100);
            }
          } else if (mapRef.current) {
            // Create overlay if it doesn't exist (shouldn't happen, but handle it)
            tooltipOverlayRef.current = createTooltipOverlay(coords, address);
            // Force redraw after creation
            if (tooltipOverlayRef.current && tooltipOverlayRef.current.draw) {
              setTimeout(() => {
                if (tooltipOverlayRef.current && tooltipOverlayRef.current.draw) {
                  tooltipOverlayRef.current.draw();
                }
              }, 100);
            }
          }
          // Ensure tooltip position state is synced with current position
          setTooltipPosition(coords);
        });
      } else {
        // Check if this request is still valid
        if (currentGeocodeRequestRef.current !== requestId) {
          return; // Request was cancelled
        }
        setIsSearchingAddress(false);
        // Show address immediately without delay
        const address = `${coords.lat}, ${coords.lng}`;
        setTooltipText(address);
        // Update currentAddress and call onLocationSelect callback
        setCurrentAddress(address);
        onLocationSelect?.(coords, address);
        if (tooltipOverlayRef.current) {
          tooltipOverlayRef.current.updateContent(address);
        }
      }
    } else {
      // Check if this request is still valid
      if (currentGeocodeRequestRef.current !== requestId) {
        return; // Request was cancelled
      }
      setIsSearchingAddress(false);
      // Show address immediately without delay
      const address = `${coords.lat}, ${coords.lng}`;
      setTooltipText(address);
      // Update currentAddress and call onLocationSelect callback
      setCurrentAddress(address);
      onLocationSelect?.(coords, address);
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.updateContent(address);
      }
    }
  };

  const reverseGeocode = async (coords, formattedAddress = null) => {
    if (formattedAddress) {
      setCurrentAddress(formattedAddress);
      return;
    }

    if (window.google?.maps) {
      // Ensure Geocoder is available, load it if needed
      let Geocoder = window.google.maps.Geocoder;
      if (!Geocoder) {
        try {
          await window.google.maps.importLibrary("geocoding");
          Geocoder = window.google.maps.Geocoder;
        } catch (err) {
          console.error("Failed to load geocoding library", err);
          setCurrentAddress(`${coords.lat}, ${coords.lng}`);
          return;
        }
      }
      
      if (Geocoder) {
        const geocoder = new Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          if (status === "OK" && results[0]) {
            setCurrentAddress(results[0].formatted_address);
          } else {
            setCurrentAddress(`${coords.lat}, ${coords.lng}`);
          }
        });
      } else {
        setCurrentAddress(`${coords.lat}, ${coords.lng}`);
      }
    } else {
      setCurrentAddress(`${coords.lat}, ${coords.lng}`);
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setPosition(coords);
        setMapCenter(coords); // Set initial map center
        mapCenterKeyRef.current = 1; // Increment key to force center update
        reverseGeocodeForTooltip(coords);
        
        // Reverse geocode and update location text box
        setTimeout(async () => {
          if (window.google?.maps) {
            // Ensure Geocoder is available, load it if needed
            let Geocoder = window.google.maps.Geocoder;
            if (!Geocoder) {
              try {
                await window.google.maps.importLibrary("geocoding");
                Geocoder = window.google.maps.Geocoder;
              } catch (err) {
                console.error("Failed to load geocoding library", err);
                const address = `${coords.lat}, ${coords.lng}`;
                setCurrentAddress(address);
                if (hideButtons) {
                  onLocationSelect?.(coords, address);
                }
                return;
              }
            }
            
            if (Geocoder) {
              const geocoder = new Geocoder();
              geocoder.geocode({ location: coords }, (results, status) => {
                const address = (status === "OK" && results[0]) 
                  ? results[0].formatted_address 
                  : `${coords.lat}, ${coords.lng}`;
                setCurrentAddress(address);
                // Only auto-update on initial load if hideButtons is true (for admin forms)
                if (hideButtons) {
                  onLocationSelect?.(coords, address);
                }
              });
            } else {
              const address = `${coords.lat}, ${coords.lng}`;
              setCurrentAddress(address);
              if (hideButtons) {
                onLocationSelect?.(coords, address);
              }
            }
          } else {
            const address = `${coords.lat}, ${coords.lng}`;
            setCurrentAddress(address);
            if (hideButtons) {
              onLocationSelect?.(coords, address);
            }
          }
        }, 500);
      },
      (err) => {
        console.error("Error getting location:", err);
        alert("Please allow location access!");
      },
      { enableHighAccuracy: true }
    );
  }, []);

  useEffect(() => {
    if (!mapRef.current || !position) {
      console.log("🟣 [USE EFFECT] Skipping attachMarker - mapRef:", !!mapRef.current, "position:", position);
      return;
    }
    
    // CRITICAL: If we just finished dragging, don't let attachMarker reset the marker position
    // Use the last dragged position instead
    if (lastDragPositionRef.current && !isDraggingRef.current) {
      const timestamp = lastDragPositionRef.current._timestamp;
      if (timestamp) {
        const timeSinceDragEnd = Date.now() - timestamp;
        console.log("🟣 [USE EFFECT] Time since drag end:", timeSinceDragEnd, "ms");
        // Only use lastDragPosition if it was set recently (within 5 seconds)
        if (timeSinceDragEnd < 5000) {
          // Check if the position state matches the last drag position
          const lastDragPos = lastDragPositionRef.current;
          const posMatches = Math.abs(position.lat - lastDragPos.lat) < 0.0001 && 
                            Math.abs(position.lng - lastDragPos.lng) < 0.0001;
          
          if (!posMatches) {
            console.log("🟣 [USE EFFECT] Recent drag detected, position state doesn't match drag position");
            console.log("🟣 [USE EFFECT] Last drag position:", lastDragPositionRef.current);
            console.log("🟣 [USE EFFECT] Position state trying to set:", position);
            console.log("🟣 [USE EFFECT] Ignoring position state update to prevent reset");
            
            // CRITICAL: Use the last drag position instead of the state position
            // This prevents the marker from being reset to the old position
            const dragPos = { lat: lastDragPos.lat, lng: lastDragPos.lng };
            attachMarker(mapRef.current, dragPos);
            console.log("🟣 [USE EFFECT] Used drag position instead:", dragPos);
            return;
          }
        }
      }
    }
    
    console.log("🟣 [USE EFFECT] Position changed, attaching marker with position:", position);
    console.log("🟣 [USE EFFECT] Current marker position before attach:", markerRef.current?.position);
    console.log("🟣 [USE EFFECT] isDraggingRef:", isDraggingRef.current);
    console.log("🟣 [USE EFFECT] lastDragPositionRef:", lastDragPositionRef.current);
    attachMarker(mapRef.current, position);
    console.log("🟣 [USE EFFECT] Marker position after attach:", markerRef.current?.position);
  }, [position]);

  // Keep refs in sync with state for use in event handlers
  useEffect(() => {
    isSearchingAddressRef.current = isSearchingAddress;
  }, [isSearchingAddress]);

  useEffect(() => {
    tooltipTextRef.current = tooltipText;
  }, [tooltipText]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  // Update tooltip pixel position when map moves, zooms, or tooltip position changes
  useEffect(() => {
    if (!mapRef.current || !tooltipPosition) return;

    const updatePosition = () => {
      if (mapRef.current && tooltipPosition) {
        updateTooltipPixelPosition(tooltipPosition);
      }
    };

    updatePosition();
    
    // Set up continuous update interval to ensure tooltip stays positioned correctly
    // This is especially important in fullscreen mode
    let continuousUpdateInterval = setInterval(() => {
      if (mapRef.current && tooltipPosition) {
        updatePosition();
      }
    }, 200); // Update every 200ms

    // Listen to map events
    const idleListener = mapRef.current.addListener("idle", updatePosition);
    const zoomListener = mapRef.current.addListener("zoom_changed", updatePosition);
    const centerListener = mapRef.current.addListener("center_changed", updatePosition);
    const resizeListener = mapRef.current.addListener("resize", updatePosition);

    // Listen to window resize events with debouncing
    let resizeTimeout;
    const handleWindowResize = () => {
      // Trigger map resize event
      if (mapRef.current && window.google?.maps?.event) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
      // Clear previous timeout
      clearTimeout(resizeTimeout);
      // Update position after a short delay
      resizeTimeout = setTimeout(() => {
        updatePosition();
      }, 150);
    };
    window.addEventListener('resize', handleWindowResize);

    // Also listen to fullscreen changes - critical for tooltip in fullscreen
    const handleFullscreenChange = () => {
      console.log("🟡 [FULLSCREEN CHANGE] Fullscreen change event fired");
      console.log("🟡 [FULLSCREEN CHANGE] Is fullscreen:", !!document.fullscreenElement);
      
      try {
        // Clear any existing interval first
        if (positionUpdateIntervalRef.current) {
          clearInterval(positionUpdateIntervalRef.current);
          positionUpdateIntervalRef.current = null;
        }
        
        // Clear all pending timeouts from previous fullscreen changes
        fullscreenTimeoutsRef.current.forEach(timeoutId => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        });
        fullscreenTimeoutsRef.current = [];
      } catch (error) {
        console.error("Error in fullscreen change handler cleanup:", error);
      }
      
      // Force immediate recalculation when fullscreen changes
      const updateSequence = () => {
        if (!mapRef.current) return;
        
        console.log("🟡 [FULLSCREEN] updateSequence called");
        
        // Trigger map resize first
        if (window.google?.maps?.event) {
          window.google.maps.event.trigger(mapRef.current, 'resize');
        }
        
        // CRITICAL: Get the ACTUAL current marker position directly from the marker
        // This ensures we show the dragged location, not the last clicked location
        let currentPosition = null;
        if (markerRef.current && markerRef.current.position) {
          const pos = markerRef.current.position;
          currentPosition = {
            lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
            lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng,
          };
          console.log("🟡 [FULLSCREEN] Marker position read:", currentPosition);
        } else {
          console.log("🟡 [FULLSCREEN] Marker position not available, markerRef:", markerRef.current);
        }
        
        // Fallback to state if marker position not available
        if (!currentPosition) {
          currentPosition = positionRef.current || position || tooltipPosition;
          console.log("🟡 [FULLSCREEN] Using fallback position from state:", currentPosition);
          console.log("🟡 [FULLSCREEN] positionRef.current:", positionRef.current);
          console.log("🟡 [FULLSCREEN] position state:", position);
          console.log("🟡 [FULLSCREEN] tooltipPosition:", tooltipPosition);
        }
        
        if (!currentPosition) {
          console.error("🟡 [FULLSCREEN] No position available!");
          return;
        }
        
        console.log("🟡 [FULLSCREEN] Final currentPosition:", currentPosition);
        
        // Determine the correct tooltip text to display
        // CRITICAL: If geocoding is in progress, ALWAYS show "Searching address…"
        // Use refs to get the latest values (they're updated immediately when state changes)
        let displayText = tooltipTextRef.current || tooltipText;
        
        // If geocoding is in progress, force "Searching address…" regardless of current tooltipText
        if (isSearchingAddressRef.current) {
          displayText = "Searching address…";
          // Also update state to ensure consistency
          if (tooltipTextRef.current !== "Searching address…") {
            setTooltipText("Searching address…");
          }
        }
        
        // CRITICAL: Update state FIRST with the actual marker position
        // Compare lat/lng values to check if position actually changed
        const positionChanged = !tooltipPosition || 
          Math.abs(currentPosition.lat - tooltipPosition.lat) > 0.000001 ||
          Math.abs(currentPosition.lng - tooltipPosition.lng) > 0.000001;
        
        // Also check if position state needs updating
        const positionStateChanged = !position || 
          Math.abs(currentPosition.lat - position.lat) > 0.000001 ||
          Math.abs(currentPosition.lng - position.lng) > 0.000001;
        
        if (positionChanged) {
          setTooltipPosition(currentPosition);
        }
        
        // CRITICAL: Always update position state to match marker position
        // This ensures the marker position persists when exiting fullscreen
        if (positionStateChanged) {
          console.log("🟡 [FULLSCREEN] Position state changed, updating state from", position, "to", currentPosition);
          setPosition(currentPosition);
          
          // Also ensure marker position is set correctly (in case it got out of sync)
          if (markerRef.current) {
            if (markerRef.current.position) {
              // For AdvancedMarkerElement, create a new LatLng object
              if (window.google?.maps?.LatLng) {
                const newLatLng = new window.google.maps.LatLng(currentPosition.lat, currentPosition.lng);
                console.log("🟡 [FULLSCREEN] Setting marker position to LatLng:", newLatLng);
                markerRef.current.position = newLatLng;
              } else {
                console.log("🟡 [FULLSCREEN] Setting marker position to object:", currentPosition);
                markerRef.current.position = currentPosition;
              }
              console.log("🟡 [FULLSCREEN] Marker position after update:", markerRef.current.position);
            } else {
              console.log("🟡 [FULLSCREEN] Marker position property not available");
            }
          } else {
            console.log("🟡 [FULLSCREEN] markerRef.current is null!");
          }
        } else {
          console.log("🟡 [FULLSCREEN] Position state unchanged:", position, "vs", currentPosition);
        }
        
        // Ensure tooltip overlay exists and is properly displayed
        if (tooltipOverlayRef.current) {
          // CRITICAL: Create a NEW position object to ensure overlay detects the change
          // This is important because the overlay might be comparing object references
          const newPosition = {
            lat: currentPosition.lat,
            lng: currentPosition.lng
          };
          
          // CRITICAL: Update overlay position FIRST with current marker position
          // Set position directly AND call updatePosition to ensure both are in sync
          tooltipOverlayRef.current.position = newPosition;
          tooltipOverlayRef.current.updatePosition(newPosition);
          
          // Then update content
          if (displayText) {
            tooltipOverlayRef.current.updateContent(displayText);
          }
          
          // Force overlay to redraw multiple times to ensure it's positioned correctly
          // This is critical after fullscreen changes when map projection might not be ready immediately
          const forceRedraw = () => {
            if (tooltipOverlayRef.current && mapRef.current) {
              // ALWAYS get fresh marker position to ensure we have the latest
              let latestPosition = newPosition;
              if (markerRef.current && markerRef.current.position) {
                const pos = markerRef.current.position;
                latestPosition = {
                  lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
                  lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng,
                };
              }
              
              // Ensure position is ALWAYS set correctly before drawing
              tooltipOverlayRef.current.position = { lat: latestPosition.lat, lng: latestPosition.lng };
              
              if (tooltipOverlayRef.current.getProjection()) {
                tooltipOverlayRef.current.draw();
              }
            }
          };
          
          // Wait a bit for map resize to complete, then start redraws
          const timeout1 = setTimeout(() => {
            forceRedraw();
            // Multiple delayed redraws to catch map resize completion
            const timeout2 = setTimeout(forceRedraw, 50);
            const timeout3 = setTimeout(forceRedraw, 100);
            const timeout4 = setTimeout(forceRedraw, 200);
            const timeout5 = setTimeout(forceRedraw, 300);
            
            // Track these timeouts for cleanup
            fullscreenTimeoutsRef.current.push(timeout2, timeout3, timeout4, timeout5);
          }, 100);
          fullscreenTimeoutsRef.current.push(timeout1);
        } else {
          // Create overlay if it doesn't exist
          // CRITICAL: If geocoding is in progress, show "Searching address…"
          const initialText = isSearchingAddressRef.current 
            ? "Searching address…" 
            : (tooltipTextRef.current || tooltipText || "Searching address…");
          
          tooltipOverlayRef.current = createTooltipOverlay(currentPosition, initialText);
          
          // Update state to match - use currentPosition from marker
          setTooltipPosition(currentPosition);
          setPosition(currentPosition);
          if (!tooltipTextRef.current || (isSearchingAddressRef.current && tooltipTextRef.current !== "Searching address…")) {
            setTooltipText(initialText);
          }
        }
        
        // Update pixel position using the current marker position (not stale tooltipPosition)
        if (currentPosition) {
          updateTooltipPixelPosition(currentPosition);
        }
      };
      
      // Start updates immediately and repeatedly to catch fullscreen transition
      updateSequence();
      const seqTimeout1 = setTimeout(updateSequence, 50);
      const seqTimeout2 = setTimeout(updateSequence, 100);
      const seqTimeout3 = setTimeout(updateSequence, 200);
      const seqTimeout4 = setTimeout(updateSequence, 300);
      const seqTimeout5 = setTimeout(updateSequence, 500);
      const seqTimeout6 = setTimeout(updateSequence, 800);
      
      // Track these timeouts for cleanup
      fullscreenTimeoutsRef.current.push(seqTimeout1, seqTimeout2, seqTimeout3, seqTimeout4, seqTimeout5, seqTimeout6);
      
      // Set up continuous updates for extended period after fullscreen change
      // This ensures tooltip stays positioned correctly during and after transition
      let updateCount = 0;
      positionUpdateIntervalRef.current = setInterval(() => {
        updateSequence();
        updateCount++;
        // Stop after 3 seconds (30 updates at 100ms intervals)
        if (updateCount >= 30) {
          if (positionUpdateIntervalRef.current) {
            clearInterval(positionUpdateIntervalRef.current);
            positionUpdateIntervalRef.current = null;
          }
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Use ResizeObserver to watch for map container size changes
    let resizeObserver;
    const mapDiv = mapRef.current.getDiv();
    if (mapDiv && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Trigger map resize event
        if (mapRef.current && window.google?.maps?.event) {
          window.google.maps.event.trigger(mapRef.current, 'resize');
        }
        // Update position after map has had time to recalculate
        setTimeout(() => {
          updatePosition();
        }, 100);
      });
      resizeObserver.observe(mapDiv);
    }

    return () => {
      if (idleListener && window.google?.maps?.event) window.google.maps.event.removeListener(idleListener);
      if (zoomListener && window.google?.maps?.event) window.google.maps.event.removeListener(zoomListener);
      if (centerListener && window.google?.maps?.event) window.google.maps.event.removeListener(centerListener);
      if (resizeListener && window.google?.maps?.event) window.google.maps.event.removeListener(resizeListener);
      window.removeEventListener('resize', handleWindowResize);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (positionUpdateIntervalRef.current) {
        clearInterval(positionUpdateIntervalRef.current);
        positionUpdateIntervalRef.current = null;
      }
      if (continuousUpdateInterval) {
        clearInterval(continuousUpdateInterval);
      }
      clearTimeout(resizeTimeout);
      
      // Clear all fullscreen-related timeouts
      fullscreenTimeoutsRef.current.forEach(timeoutId => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
      fullscreenTimeoutsRef.current = [];
    };
  }, [tooltipPosition]);

  // Update tooltip overlay when text or position changes
  useEffect(() => {
    if (!mapRef.current || !tooltipPosition) return;
    
    // Ensure tooltip overlay exists and is properly displayed
    if (tooltipOverlayRef.current && tooltipText && tooltipPosition) {
      tooltipOverlayRef.current.updateContent(tooltipText);
      tooltipOverlayRef.current.updatePosition(tooltipPosition);
      // Force redraw to ensure it's visible after fullscreen changes
      if (tooltipOverlayRef.current.draw) {
        tooltipOverlayRef.current.draw();
      }
    } else if (mapRef.current && tooltipText && tooltipPosition && !tooltipOverlayRef.current) {
      // Create overlay if it doesn't exist
      tooltipOverlayRef.current = createTooltipOverlay(tooltipPosition, tooltipText);
    }
  }, [tooltipText, tooltipPosition]);

  // Update position when external position changes (from autocomplete selection)
  useEffect(() => {
    // CRITICAL: Don't reset position if we just finished dragging
    if (lastDragPositionRef.current && !isDraggingRef.current) {
      const timestamp = lastDragPositionRef.current._timestamp;
      if (timestamp) {
        const timeSinceDragEnd = Date.now() - timestamp;
        console.log("🟪 [EXTERNAL POSITION] Time since drag end:", timeSinceDragEnd, "ms");
        if (timeSinceDragEnd < 5000) { // Increased to 5 seconds
          console.log("🟪 [EXTERNAL POSITION] Ignoring externalPosition update - recent drag detected");
          console.log("🟪 [EXTERNAL POSITION] Last drag position:", lastDragPositionRef.current);
          console.log("🟪 [EXTERNAL POSITION] External position trying to set:", externalPosition);
          return;
        }
      }
    }
    
    // CRITICAL: Also check if marker position differs from externalPosition
    // If marker is at a different position than externalPosition, don't reset it
    // This prevents externalPosition from overriding user's drag action
    if (markerRef.current && markerRef.current.position && externalPosition) {
      const markerPos = markerRef.current.position;
      const markerLat = typeof markerPos.lat === 'function' ? markerPos.lat() : markerPos.lat;
      const markerLng = typeof markerPos.lng === 'function' ? markerPos.lng() : markerPos.lng;
      
      const posDiff = Math.abs(markerLat - externalPosition.lat) > 0.0001 || 
                      Math.abs(markerLng - externalPosition.lng) > 0.0001;
      
      if (posDiff && lastDragPositionRef.current) {
        console.log("🟪 [EXTERNAL POSITION] Marker position differs from externalPosition");
        console.log("🟪 [EXTERNAL POSITION] Marker:", { lat: markerLat, lng: markerLng });
        console.log("🟪 [EXTERNAL POSITION] External:", externalPosition);
        console.log("🟪 [EXTERNAL POSITION] Ignoring - user has dragged marker to different location");
        return;
      }
    }
    
    if (externalPosition && externalPosition.lat && externalPosition.lng) {
      // Prevent infinite loop: check if this is the same external position as before
      if (lastExternalPositionRef.current) {
        const lastExt = lastExternalPositionRef.current;
        const isSame = Math.abs(lastExt.lat - externalPosition.lat) < 0.0001 &&
                      Math.abs(lastExt.lng - externalPosition.lng) < 0.0001;
        if (isSame) {
          console.log("🟪 [EXTERNAL POSITION] Same external position, ignoring to prevent loop");
          return;
        }
      }
      
      console.log("🟪 [EXTERNAL POSITION] External position changed:", externalPosition);
      
      // Store the external position to prevent loops
      lastExternalPositionRef.current = {
        lat: externalPosition.lat,
        lng: externalPosition.lng
      };
      
      // CRITICAL: Check if this externalPosition matches the current marker position
      // If marker is at a different position (from drag), don't reset it
      if (markerRef.current && markerRef.current.position) {
        const markerPos = markerRef.current.position;
        const markerLat = typeof markerPos.lat === 'function' ? markerPos.lat() : markerPos.lat;
        const markerLng = typeof markerPos.lng === 'function' ? markerPos.lng() : markerPos.lng;
        
        const posDiff = Math.abs(markerLat - externalPosition.lat) > 0.0001 || 
                        Math.abs(markerLng - externalPosition.lng) > 0.0001;
        
        if (posDiff && lastDragPositionRef.current) {
          console.log("🟪 [EXTERNAL POSITION] Marker at different position, ignoring externalPosition");
          console.log("🟪 [EXTERNAL POSITION] Marker:", { lat: markerLat, lng: markerLng });
          console.log("🟪 [EXTERNAL POSITION] External:", externalPosition);
          // Don't reset - user has dragged to a different location
          return;
        }
      }
      
      const coords = { lat: externalPosition.lat, lng: externalPosition.lng };
      
      // Only update position if it actually changed to prevent unnecessary re-renders
      if (!position || Math.abs(position.lat - coords.lat) > 0.0001 || Math.abs(position.lng - coords.lng) > 0.0001) {
        setPosition(coords);
      }
      
      // Update map center when external position changes (for autocomplete)
      setMapCenter(coords);
      mapCenterKeyRef.current += 1; // Increment key to force center update
      
      reverseGeocodeForTooltip(coords);
      
      // Center the map on the new position using panTo (doesn't trigger center prop)
      // Only set zoom if user hasn't manually zoomed, otherwise preserve their zoom level
      if (mapRef.current) {
        mapRef.current.panTo(coords);
        // Only reset zoom if user hasn't manually zoomed in
        if (!userHasZoomedRef.current) {
          mapRef.current.setZoom(15);
          setMapZoom(15);
        }
      }
    }
  }, [externalPosition]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (position) {
      // Set map center if not already set
      if (!mapCenter) {
        setMapCenter(position);
        mapCenterKeyRef.current = 1; // Set key for initial center
      }
      attachMarker(map, position);
      // Create tooltip overlay if we have position and text
      if (tooltipText && !tooltipOverlayRef.current) {
        tooltipOverlayRef.current = createTooltipOverlay(position, tooltipText);
      }
    }
    // After map loads, mark that initial center is set
    // This prevents center prop from being applied again
    hasSetInitialCenterRef.current = true;
  };

  const handleMapUnmount = () => {
    // Clear address timeout
    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
      addressTimeoutRef.current = null;
    }
    
    // Remove tooltip overlay
    if (tooltipOverlayRef.current) {
      tooltipOverlayRef.current.setMap(null);
      tooltipOverlayRef.current = null;
    }
    
    if (markerRef.current) {
      // Remove event listeners if they exist
      if (markerRef.current._listeners && window.google?.maps?.event) {
        markerRef.current._listeners.forEach(listener => {
          window.google.maps.event.removeListener(listener);
        });
      }
      if (markerRef.current.map !== undefined) {
        markerRef.current.map = null;
      }
      if (markerRef.current.setMap) {
        markerRef.current.setMap(null);
      }
      markerRef.current = null;
    }
    mapRef.current = null;
  };

  if (!isLoaded) return <div>Loading Google Maps...</div>;
  if (!position) return <div>Detecting your current location...</div>;

  return (
    <div className="d-flex flex-column" style={{ gap: hideButtons ? "16px" : "16px" }}>
      {/* User instruction message */}
      <div 
        className="alert alert-info" 
        style={{ 
          marginBottom: "0",
          padding: "12px 16px",
          fontSize: "14px",
          borderRadius: "8px"
        }}
      >
        <strong>💡 Tip:</strong> You can move the cursor on the map to select your location. The location will be automatically updated in the location field.
      </div>

      {!hideButtons && showInput && (
        <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
          <input
            type="text"
            placeholder="Search location..."
            className="form-input"
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
          />
        </Autocomplete>
      )}

      {!hideButtons && (
        <div className="d-flex" style={{ gap: "10px" }}>
          {!showInput && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => {
                setShowInput(true);
              }}
            >
              Select
            </button>
          )}
        </div>
      )}

      <div 
        ref={mapContainerRef}
        style={{ position: "relative", width: "100%", height: containerStyle.height, overflow: "visible" }}
      >
        {mapCenter || position ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter || position}
            zoom={mapZoom}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
            onUnmount={handleMapUnmount}
            onZoomChanged={() => {
              // Track when user manually zooms
              if (mapRef.current) {
                const currentZoom = mapRef.current.getZoom();
                if (currentZoom !== null && currentZoom !== undefined) {
                  setMapZoom(currentZoom);
                  userHasZoomedRef.current = true;
                }
              }
            }}
            onCenterChanged={() => {
              // When user pans the map, mark that initial center is set
              // DON'T update mapCenter state here - that would cause the center prop to update and reset the map
              // The center prop will stay at the initial value, but Google Maps won't reset because
              // it only resets if the center prop value actually changes
              hasSetInitialCenterRef.current = true;
            }}
            options={{
              ...(mapId ? { mapId } : {}),
              gestureHandling: "greedy",
              draggable: true,
              scrollwheel: true,
              disableDoubleClickZoom: false,
            }}
          />
        ) : (
          <div style={{ width: "100%", height: containerStyle.height, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0" }}>
            <div>Loading map...</div>
          </div>
        )}
        {/* Tooltip is handled by OverlayView - no fallback div needed to prevent duplicates */}
      </div>
    </div>
  );
};

export default LocationPicker;

